/**
 * CSS custom-property REFERENCE grammar: the shape a `var(--token)` reference takes inside a design-token string,
 * for the shared React hooks that must resolve a css-refs value to its raw computed value at runtime. Distinct from
 * `const/design-tokens-css-prefixes.ts`, which declares the `--<group>` prefix namespace the token pipeline EMITS;
 * this leaf parses a reference rather than composing a name. Homed in `const/` because the consumers are
 * `components/` leaves and `const/` is the one layer every tier may import.
 *
 * Paired forbid-rule for this SSOT class: `QUAL-CROSS-FILE-REGEX-CONST-DUP-001` (its cross-file requirement and the
 * single-file gap that follows from it are recorded on that leaf).
 */

/**
 * A `var(--name)` reference, capturing the custom-property NAME (leading `--` included) in group 1. A COMPILED
 * instance is shared safely because the shape is flagless and every consumer drives it with `.exec()`, which touches
 * `lastIndex` only under `/g` or `/y`.
 */
export const CSS_VAR_NAME_REGEX = /var\((--[\w-]+)\)/;

/**
 * A WHOLE declaration value that is NOTHING BUT a custom-property read -- `var(--x)`, `var( --x , fallback )` -- i.e.
 * a token of a scoped vocabulary rather than an authored literal. Anchored at both ends, tolerant of the whitespace
 * and the fallback tail prettier and hand-authoring both produce, and case-INSENSITIVE because CSS keywords are.
 *
 * Distinct from {@link CSS_VAR_NAME_REGEX} above, which is a token PROBE that captures the name out of a longer value;
 * this one answers "is the entire value a token read", the exemption gate every hardcoded-value design-system rule
 * needs before it convicts a residue. Distinct too from `CSS_TOKEN_REF_REGEX` (`const/regexes.ts`), whose class admits
 * only `[a-z0-9-]` and no fallback -- adopting that one here would convict `var(--x, 0)` as a literal.
 *
 * Shared as a COMPILED instance: `/i` carries no `lastIndex`, and every consumer drives it with `.test()`.
 */
export const WHOLE_VALUE_CSS_VAR_REGEX = /^var\(\s*--[\w-]+[^)]*\)$/i;
